import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { contentAPI } from '../services/api';
import useNavigationWarning from '../hooks/useNavigationWarning';
import { checkAuthBeforeAction } from '../utils/authHelper';
import NavigationWarningModal from '../components/NavigationWarningModal';
import usePermission from '../hooks/usePermission';
import UpgradePrompt from '../components/UpgradePrompt';

// Square Icon SVG Component - Simple and clean square
const SquareIcon = ({ className = "w-8 h-8" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="2" fill="none"/>
  </svg>
);

const PosterGeneration = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Permission check
  const { hasFeatureAccess, getPlanInfo, getRequiredPlanForFeature } = usePermission();
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradePromptData, setUpgradePromptData] = useState({});

  // Form states
  const [title, setTitle] = useState('');
  const [subTitle, setSubTitle] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [promptText, setPromptText] = useState('');
  const [language, setLanguage] = useState('english');
  const [aspectRatio, setAspectRatio] = useState('portrait');
  const [loraStyle, setLoraStyle] = useState('modern');
  const [loraWeight, setLoraWeight] = useState(0.8);
  const [numImages, setNumImages] = useState(1);

  // UI states
  const [generating, setGenerating] = useState(false);
  const [generatedPosters, setGeneratedPosters] = useState([]);
  const [error, setError] = useState('');
  const [selectedPoster, setSelectedPoster] = useState(null);
  const [optimizingTitle, setOptimizingTitle] = useState(false);

  const navigationWarning = useNavigationWarning(generating || optimizingTitle, 'A poster is being generated. If you leave now, the generation will continue in the background. Are you sure you want to leave?', 'image-generation');

  // Load prefill data from Workspace if available
  useEffect(() => {
    if (location.state?.prefillData) {
      const {
        title: prefillTitle,
        subTitle: prefillSubTitle,
        bodyText: prefillBodyText,
        prompt: prefillPrompt,
        previousImage
      } = location.state.prefillData;

      if (prefillTitle) setTitle(prefillTitle);
      if (prefillSubTitle) setSubTitle(prefillSubTitle);
      if (prefillBodyText) setBodyText(prefillBodyText);
      if (prefillPrompt) setPromptText(prefillPrompt);

      if (previousImage) {
        setGeneratedPosters([{
          imageUrl: previousImage,
          title: prefillTitle || '',
          subTitle: prefillSubTitle || ''
        }]);
        setSelectedPoster(0);
      }

      // Clear the navigation state to prevent re-filling on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const aspectRatios = [
    { value: 'portrait', label: 'Portrait', subtitle: '9:16', icon: '▯', iconType: 'text' },
    { value: 'landscape', label: 'Landscape', subtitle: '16:9', icon: '▭', iconType: 'text' },
    { value: 'square', label: 'Square', subtitle: '1:1', icon: <SquareIcon />, iconType: 'svg' }
  ];

  const loraStyles = [
    { value: 'modern', label: 'Modern', description: 'Clean and contemporary design' },
    { value: 'vintage', label: 'Vintage', description: 'Retro and nostalgic style' },
    { value: 'cartoon', label: 'Cartoon', description: 'Playful cartoon illustrations' },
    { value: 'watercolor', label: 'Watercolor', description: 'Artistic watercolor painting' },
    { value: 'oil-painting', label: 'Oil Painting', description: 'Classic oil painting style' },
    { value: 'minimalist', label: 'Minimalist', description: 'Simple and elegant design' },
    { value: 'festive', label: 'Festive', description: 'Celebration and holiday theme' },
    { value: 'professional', label: 'Professional', description: 'Business and corporate style' }
  ];

  const languages = [
    // European Languages (Latin Script)
    { value: 'english', label: 'English', flag: '🇬🇧' },
    { value: 'spanish', label: 'Español', flag: '🇪🇸' },
    { value: 'french', label: 'Français', flag: '🇫🇷' },
    { value: 'german', label: 'Deutsch', flag: '🇩🇪' },
    { value: 'italian', label: 'Italiano', flag: '🇮🇹' },
    { value: 'portuguese', label: 'Português', flag: '🇵🇹' },
    { value: 'dutch', label: 'Nederlands', flag: '🇳🇱' },
    { value: 'polish', label: 'Polski', flag: '🇵🇱' },

    // Cyrillic Script
    { value: 'russian', label: 'Русский', flag: '🇷🇺' },
    { value: 'ukrainian', label: 'Українська', flag: '🇺🇦' },

    // Asian Languages
    { value: 'chinese', label: '中文', flag: '🇨🇳' },
    { value: 'japanese', label: '日本語', flag: '🇯🇵' },
    { value: 'korean', label: '한국어', flag: '🇰🇷' },
    { value: 'thai', label: 'ไทย', flag: '🇹🇭' },
    { value: 'vietnamese', label: 'Tiếng Việt', flag: '🇻🇳' },
    { value: 'indonesian', label: 'Bahasa', flag: '🇮🇩' },

    // Middle Eastern & South Asian
    { value: 'arabic', label: 'العربية', flag: '🇸🇦' },
    { value: 'hebrew', label: 'עברית', flag: '🇮🇱' },
    { value: 'persian', label: 'فارسی', flag: '🇮🇷' },
    { value: 'urdu', label: 'اردو', flag: '🇵🇰' },
    { value: 'hindi', label: 'हिन्दी', flag: '🇮🇳' },

    // Scandinavian Languages
    { value: 'swedish', label: 'Svenska', flag: '🇸🇪' },
    { value: 'norwegian', label: 'Norsk', flag: '🇳🇴' },
    { value: 'danish', label: 'Dansk', flag: '🇩🇰' },
    { value: 'finnish', label: 'Suomi', flag: '🇫🇮' },

    // Other Languages
    { value: 'turkish', label: 'Türkçe', flag: '🇹🇷' },
    { value: 'greek', label: 'Ελληνικά', flag: '🇬🇷' },
    { value: 'czech', label: 'Čeština', flag: '🇨🇿' },
    { value: 'romanian', label: 'Română', flag: '🇷🇴' },
    { value: 'hungarian', label: 'Magyar', flag: '🇭🇺' },
    { value: 'bulgarian', label: 'Български', flag: '🇧🇬' }
  ];

  const handleOptimizeTitle = async () => {
    if (!title.trim()) {
      setError('Please enter a title first');
      setTimeout(() => setError(''), 3000);
      return;
    }

    // Check feature permission
    if (!hasFeatureAccess('poster')) {
      const planInfo = getPlanInfo();
      setUpgradePromptData({
        type: 'feature',
        featureName: 'Poster Generation',
        currentPlan: planInfo.name,
        requiredPlan: getRequiredPlanForFeature('poster')
      });
      setShowUpgradePrompt(true);
      return;
    }

    setOptimizingTitle(true);
    setError('');

    try {
      const response = await contentAPI.optimizePosterTitle({
        title: title.trim()
      });

      // Handle axios response format - response.data contains the actual data
      const data = response.data || response;

      if (data.success && data.data?.optimizedTitle) {
        setTitle(data.data.optimizedTitle);
      } else if (data.optimizedTitle) {
        // Fallback if structure is different
        setTitle(data.optimizedTitle);
      } else {
        throw new Error(data.error?.message || data.message || 'Failed to optimize title');
      }
    } catch (err) {
      console.error('Title optimization error:', err);
      console.error('Error response:', err.response?.data);
      const errorMessage = err.response?.data?.error?.message
        || err.response?.data?.message
        || err.message
        || 'Failed to optimize title. Please try again.';
      setError(errorMessage);
      setTimeout(() => setError(''), 3000);
    } finally {
      setOptimizingTitle(false);
    }
  };

  const handleGenerate = async () => {
    // Check authentication before making API call
    if (!checkAuthBeforeAction(navigate)) {
      return;
    }

    if (!title.trim()) {
      setError('Please enter a title for your poster');
      setTimeout(() => setError(''), 3000);
      return;
    }

    // Check feature permission
    if (!hasFeatureAccess('poster')) {
      const planInfo = getPlanInfo();
      setUpgradePromptData({
        type: 'feature',
        featureName: 'Poster Generation',
        currentPlan: planInfo.name,
        requiredPlan: getRequiredPlanForFeature('poster')
      });
      setShowUpgradePrompt(true);
      return;
    }

    setGenerating(true);
    setError('');
    setGeneratedPosters([]);
    setSelectedPoster(null);

    try {
      // Step 1: Optimize prompt using DeepSeek based on language
      console.log('Step 1: Optimizing prompt for language:', language);

      const optimizeResponse = await contentAPI.optimizePosterPrompt({
        title: title.trim(),
        subTitle: subTitle.trim(),
        bodyText: bodyText.trim(),
        promptText: promptText.trim(),
        language,
        loraStyle
      });

      const optimizeData = optimizeResponse.data || optimizeResponse;
      const optimizedPrompt = optimizeData.data?.optimizedPrompt || optimizeData.optimizedPrompt;

      console.log('Optimized prompt:', optimizedPrompt);

      // Step 2: Generate poster with optimized prompt
      console.log('Step 2: Generating poster with optimized prompt');

      const response = await contentAPI.generatePoster({
        title: title.trim(),
        subTitle: subTitle.trim(),
        bodyText: bodyText.trim(),
        promptText: optimizedPrompt, // Use optimized prompt
        language,
        aspectRatio,
        loraStyle,
        loraWeight,
        numImages
      });

      // Handle axios response format - response.data contains the actual data
      const data = response.data || response;

      if (data.success && data.data?.posters) {
        setGeneratedPosters(data.data.posters);
        setSelectedPoster(0);
      } else if (data.posters) {
        // Fallback if structure is different
        setGeneratedPosters(data.posters);
        setSelectedPoster(0);
      } else {
        throw new Error(data.error?.message || data.message || 'Failed to generate poster');
      }
    } catch (err) {
      console.error('Poster generation error:', err);
      console.error('Error response:', err.response?.data);
      const errorMessage = err.response?.data?.error?.message
        || err.response?.data?.message
        || err.message
        || 'Failed to generate poster. Please try again.';
      setError(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = (posterUrl, index) => {
    const link = document.createElement('a');
    link.href = posterUrl;
    link.download = `poster_${Date.now()}_${index + 1}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <NavigationWarningModal
        isOpen={navigationWarning.showModal}
        onCancel={navigationWarning.handleCancelLeave}
        onConfirm={navigationWarning.handleConfirmLeave}
        message={navigationWarning.warningMessage}
        taskType={navigationWarning.taskType}
      />

      <UpgradePrompt
        show={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        type={upgradePromptData.type}
        featureName={upgradePromptData.featureName}
        currentPlan={upgradePromptData.currentPlan}
        requiredPlan={upgradePromptData.requiredPlan}
      />

      <div className="flex h-screen bg-gray-50">
        <Sidebar activeCategory="poster" />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />

        <main className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            {/* Page Header - Simplified */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900">
                AI Poster Generator
              </h1>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Input Form */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="space-y-5">
                  {/* Title Input */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-900">
                        Title *
                      </label>
                      {title.trim() && (
                        <button
                          onClick={handleOptimizeTitle}
                          disabled={optimizingTitle}
                          className="flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-medium rounded-full hover:from-purple-600 hover:to-pink-600 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {optimizingTitle ? (
                            <>
                              <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span>Optimizing...</span>
                            </>
                          ) : (
                            <>
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                              <span>AI Optimize</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="e.g., Christmas"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                      maxLength={500}
                    />
                    <p className="mt-1 text-xs text-gray-400">{title.length}/500</p>
                  </div>

                  {/* Subtitle & Body Text in Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Subtitle
                      </label>
                      <input
                        type="text"
                        value={subTitle}
                        onChange={(e) => setSubTitle(e.target.value)}
                        placeholder="Optional"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        maxLength={100}
                      />
                      <p className="mt-1 text-xs text-gray-400">{subTitle.length}/100</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Visual Elements
                      </label>
                      <input
                        type="text"
                        value={promptText}
                        onChange={(e) => setPromptText(e.target.value)}
                        placeholder="e.g., flowers, balloons"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>

                  {/* Body Text */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Description
                    </label>
                    <textarea
                      value={bodyText}
                      onChange={(e) => setBodyText(e.target.value)}
                      placeholder="Optional promotional message..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none text-sm"
                      maxLength={200}
                    />
                    <p className="mt-1 text-xs text-gray-400">{bodyText.length}/200</p>
                  </div>

                  {/* Aspect Ratio & Style in Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Aspect Ratio
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        {aspectRatios.map((ratio) => (
                          <button
                            key={ratio.value}
                            onClick={() => setAspectRatio(ratio.value)}
                            className={`p-2 rounded-lg border-2 transition text-center ${
                              aspectRatio === ratio.value
                                ? 'border-primary-500 bg-primary-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                          >
                            <div className="flex justify-center mb-1">
                              {ratio.iconType === 'svg' ? (
                                <div className="w-5 h-5 flex items-center justify-center">
                                  {React.cloneElement(ratio.icon, { className: "w-full h-full" })}
                                </div>
                              ) : (
                                <div className="text-lg">{ratio.icon}</div>
                              )}
                            </div>
                            <div className="text-xs font-medium text-gray-900">{ratio.subtitle}</div>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Style Strength
                      </label>
                      <div className="pt-2">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs text-gray-500">Subtle</span>
                          <span className="text-xs font-medium text-gray-900">{loraWeight.toFixed(1)}</span>
                          <span className="text-xs text-gray-500">Strong</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={loraWeight}
                          onChange={(e) => setLoraWeight(parseFloat(e.target.value))}
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Style Selection - Compact */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Poster Style
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {loraStyles.map((style) => (
                        <button
                          key={style.value}
                          onClick={() => setLoraStyle(style.value)}
                          className={`p-2 rounded-lg border-2 transition text-center ${
                            loraStyle === style.value
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="text-xs font-medium text-gray-900">
                            {style.label}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Language Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-2">
                      Poster Text Language
                    </label>
                    <div className="grid grid-cols-6 gap-2 max-h-48 overflow-y-auto pr-2">
                      {languages.map((lang) => (
                        <button
                          key={lang.value}
                          onClick={() => setLanguage(lang.value)}
                          className={`p-2 rounded-lg border-2 transition text-center ${
                            language === lang.value
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          title={lang.label}
                        >
                          <div className="text-lg mb-0.5">{lang.flag}</div>
                          <div className="text-[10px] font-medium text-gray-900 leading-tight truncate">
                            {lang.label}
                          </div>
                        </button>
                      ))}
                    </div>
                    <p className="mt-2 text-xs text-gray-500">
                      30 languages supported • AI optimizes for selected language
                    </p>
                  </div>

                  {/* Generate Button */}
                  <button
                    onClick={handleGenerate}
                    disabled={generating || !title.trim()}
                    className="w-full bg-gradient-to-r from-primary-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-primary-700 hover:to-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {generating ? (
                      <>
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>Generate Poster</span>
                      </>
                    )}
                  </button>

                  {/* Error Message */}
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                      {error}
                    </div>
                  )}

                  {/* Hidden input */}
                  <input type="hidden" value={1} />
                </div>
              </div>

              {/* Right Column - Preview/Results */}
              <div className="lg:sticky lg:top-8 h-fit">
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Preview
                  </h2>

                  {generating && (
                    <div className="flex flex-col items-center justify-center py-20">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-3"></div>
                      <p className="text-gray-600 text-sm">Creating your poster...</p>
                    </div>
                  )}

                  {!generating && generatedPosters.length === 0 && (
                    <div className="text-center py-20">
                      <svg className="mx-auto h-12 w-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-gray-400 text-sm">No poster yet</p>
                      <p className="text-xs text-gray-400 mt-1">Enter a title and click Generate</p>
                    </div>
                  )}

                  {!generating && generatedPosters.length > 0 && (
                    <div className="space-y-3">
                      {/* Main Preview */}
                      <div className="relative group">
                        <img
                          src={generatedPosters[selectedPoster]?.imageUrl}
                          alt="Generated poster"
                          className="w-full rounded-lg shadow-lg"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                          <button
                            onClick={() => handleDownload(generatedPosters[selectedPoster]?.imageUrl, selectedPoster)}
                            className="bg-white text-gray-900 px-4 py-2 rounded-lg font-medium hover:bg-gray-100 transition text-sm"
                          >
                            Download
                          </button>
                        </div>
                      </div>

                      {/* Thumbnails */}
                      {generatedPosters.length > 1 && (
                        <div className="grid grid-cols-4 gap-2">
                          {generatedPosters.map((poster, index) => (
                            <button
                              key={index}
                              onClick={() => setSelectedPoster(index)}
                              className={`relative rounded-lg overflow-hidden border-2 transition ${
                                selectedPoster === index
                                  ? 'border-primary-500'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <img
                                src={poster.imageUrl}
                                alt={`Poster ${index + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Download All Button */}
                      {generatedPosters.length > 1 && (
                        <button
                          onClick={() => {
                            generatedPosters.forEach((poster, index) => {
                              setTimeout(() => handleDownload(poster.imageUrl, index), index * 500);
                            });
                          }}
                          className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 py-2 px-4 rounded-lg font-medium transition text-sm"
                        >
                          Download All ({generatedPosters.length})
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
      </div>
    </>
  );
};

export default PosterGeneration;
